import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/common/SignaturePad";
import { CheckCircle2, AlertTriangle, Loader2, Eraser, PenLine } from "lucide-react";

export default function SignPhone() {
  const { token } = useParams();
  const pad = useRef(null);
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/sign-session/${token}`).then((r) => { setInfo(r.data); setState("ready"); })
      .catch((e) => { setError(e.response?.data?.detail || "This signing link is invalid."); setState("error"); });
  }, [token]);

  const submit = async () => {
    setState("sending");
    try { await axios.post(`${API}/public/sign-session/${token}`, { data_url: pad.current.toDataURL() }); setState("done"); }
    catch (e) { setError(e.response?.data?.detail || "Couldn't submit the signature."); setState("error"); }
  };

  if (state === "loading") return <div className="flex min-h-screen items-center justify-center bg-[#F4FBF7]"><Loader2 className="h-6 w-6 animate-spin text-[#00A85A]" /></div>;
  return (
    <div className="min-h-screen bg-[#F4FBF7] px-4 py-8" data-testid="sign-phone-page">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-5 shadow-sm">
        {state === "error" && <div className="text-center" data-testid="sign-phone-error"><AlertTriangle className="mx-auto h-8 w-8 text-red-500" /><p className="mt-2 text-sm font-semibold text-[#101516]">{error}</p></div>}
        {state === "done" && <div className="text-center" data-testid="sign-phone-done"><CheckCircle2 className="mx-auto h-10 w-10 text-[#00A85A]" /><p className="mt-2 text-base font-bold text-[#101516]">Signature sent</p><p className="text-sm text-[#687173]">It now appears on the consent form. You can close this page.</p></div>}
        {["ready", "sending"].includes(state) && info && (
          <>
            <div className="text-xs font-bold uppercase tracking-wider text-[#00A85A]">{info.clinic_name}</div>
            <h1 className="mt-1 text-lg font-bold text-[#101516]">{info.title}</h1>
            <p className="mt-1 text-sm text-[#687173]">{info.role === "doctor" ? `Doctor signature · ${info.doctor_name || ""}` : `Patient signature · ${info.patient_name || ""}`}</p>
            <div className="mt-4"><SignaturePad ref={pad} height={220} onChange={(e) => setDrawn(!e)} testid="sign-phone-pad" /></div>
            <div className="mt-3 flex gap-2">
              <Button variant="outline" onClick={() => pad.current.clear()} data-testid="sign-phone-clear"><Eraser className="mr-1.5 h-4 w-4" />Clear</Button>
              <Button disabled={!drawn || state === "sending"} onClick={submit} className="flex-1 bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid="sign-phone-submit">{state === "sending" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />}Submit signature</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
